import { ActionTypes } from "./session.types";
import { IDispatch } from "redux/store";
import Cookies from "js-cookie";
import axios from "services/axios.service";
import { endpoints } from "config/endpoints";
import { getFingerprint } from "utils/misc/fingerprint";
import { AntiFraudType } from "utils/types/session";

type HttpError = {
  response?: { status?: number };
  request?: { status?: number };
};

export const decodeJWTPayload = (token: string) => {
  const payload = token.split(".")[1];
  if (!payload) {
    throw new Error("Invalid JWT payload");
  }

  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const paddedBase64 = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "="
  );

  return JSON.parse(atob(paddedBase64));
};

const isRejectedSession = (error: unknown) => {
  const httpError = error as HttpError;
  const status = httpError?.response?.status ?? httpError?.request?.status;

  return status === 401 || status === 403;
};

const getRequiredFingerprint = async (antiFraudType: AntiFraudType) => {
  const fingerprint = await getFingerprint(antiFraudType);
  if (!fingerprint.fingerprintId) {
    throw new Error("Fingerprint collection did not return an identifier");
  }

  return fingerprint;
};

const getNewSession = async (antiFraudType: AntiFraudType, lang: string) => {
  const params: {
    lang: string;
    fId?: string;
    fType?: AntiFraudType;
    fRequestId?: string;
  } = { lang };
  if (antiFraudType) {
    const fingerprint = await getRequiredFingerprint(antiFraudType);
    params.fId = fingerprint.fingerprintId;
    params.fType = fingerprint.fingerprintType;
    if (fingerprint.fingerprintRequestId) {
      params.fRequestId = fingerprint.fingerprintRequestId;
    }
  }
  await axios.api.get(`${endpoints.api.user.newSession}?`, {
    params,
  });
};

const checkCurrentSession = async (antiFraudType: AntiFraudType) => {
  // console.log("checkCurrentSession - Called");
  await axios.api.get(`${endpoints.api.user.regenerateToken}`);
  const token = Cookies.get("jwt");
  if (antiFraudType && token) {
    const currentUser = decodeJWTPayload(token);

    if (!currentUser["f"]) {
      const fingerprint = await getRequiredFingerprint(antiFraudType);
      await axios.api.put(endpoints.api.user.setData, fingerprint);
    }
  }
};

export const setSession =
  (lang: string, antiFraudType: AntiFraudType) =>
  async (dispatch: IDispatch) => {
    console.log("setSession - Called");
    try {
      let token = Cookies.get("jwt");
      console.log("setSession - Token", token);
      if (!token) {
        await getNewSession(antiFraudType, lang);
      } else {
        try {
          await checkCurrentSession(antiFraudType);
        } catch (error) {
          console.log(
            "setSession - Error while executing checkCurrentSession",
            error
          );
          if (isRejectedSession(error)) {
            await getNewSession(antiFraudType, lang);
          } else if (antiFraudType) {
            const currentToken = Cookies.get("jwt");
            if (!currentToken || !decodeJWTPayload(currentToken)["f"]) {
              throw error;
            }
          }
        }
      }

      const sessionToken = Cookies.get("jwt");
      if (!sessionToken) {
        throw new Error("Session endpoint did not set the JWT cookie");
      }
      if (antiFraudType && !decodeJWTPayload(sessionToken)["f"]) {
        throw new Error("Session is not protected by a fingerprint");
      }

      dispatch({
        type: ActionTypes.SET_SESSION,
        payload: sessionToken,
      });
      return true;
    } catch (err) {
      console.log("setSession - Error while setting a session", err);
      return false;
    }
  };
